import React, { useEffect, useState } from 'react';
import { Smartphone, Loader2, CheckCircle2, Wifi, CreditCard, RefreshCw } from 'lucide-react';
import {
  isTapToPayAvailable,
  isTapToPayComingSoon,
  isTapToPaySetUp,
  setupTapToPay,
} from '../services/tapToPayService';

/**
 * One-time Tap to Pay on iPhone setup card (Stripe integration settings).
 * "Enable Tap to Pay on iPhone" connects this device as a Stripe Terminal
 * reader without charging anything, which makes iOS show Apple's Tap to Pay
 * terms and run the configuration download, then confirms the device is ready.
 */
const TapToPaySetup: React.FC<{ businessId: string | null }> = ({ businessId }) => {
  // 'checking' → 'unsupported' (web/Android) | 'coming-soon' | 'idle' | 'enabling' | 'ready'
  const [state, setState] = useState<'checking' | 'unsupported' | 'coming-soon' | 'idle' | 'enabling' | 'ready'>('checking');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const available = await isTapToPayAvailable();
      if (cancelled) return;
      if (available) {
        setState(businessId && isTapToPaySetUp(businessId) ? 'ready' : 'idle');
        return;
      }
      setState((await isTapToPayComingSoon()) ? 'coming-soon' : 'unsupported');
    })();
    return () => { cancelled = true; };
  }, [businessId]);

  const handleEnable = async () => {
    if (!businessId || state === 'enabling') return;
    setError(null);
    setState('enabling');
    try {
      await setupTapToPay(businessId);
      setState('ready');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Setup failed — please try again.';
      // Dismissing Apple's terms sheet cancels the connect — back to the button.
      if (!/cancel/i.test(msg)) setError(msg);
      setState('idle');
    }
  };

  const education = (
    <div className="space-y-2">
      <div className="flex items-start gap-2.5">
        <Wifi className="h-4 w-4 text-purple-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-gray-600">
          Accept contactless credit and debit cards, Apple Pay and other digital wallets — no extra hardware, this iPhone is the card reader.
        </p>
      </div>
      <div className="flex items-start gap-2.5">
        <CreditCard className="h-4 w-4 text-purple-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-gray-600">
          At checkout, your customer holds their card or phone against the top of your iPhone until it confirms.
        </p>
      </div>
    </div>
  );

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-1.5">
        <Smartphone className="h-4 w-4 text-gray-700" />
        Tap to Pay on iPhone
      </h3>
      <p className="text-xs text-gray-500 mb-3">
        Take in-person payments with just your iPhone.
      </p>

      {state === 'checking' && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        </div>
      )}

      {state === 'unsupported' && (
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-xs text-gray-500">
            Tap to Pay is set up from the Nexus iOS app. Open Settings → Integrations → Stripe on your iPhone (XS or later) to enable it.
          </p>
        </div>
      )}

      {state === 'coming-soon' && (
        <div className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 text-center">
          <Smartphone className="h-5 w-5 text-gray-400" />
          <span className="text-xs font-semibold text-blue-600 bg-blue-50 rounded-full px-2.5 py-0.5">Coming soon</span>
        </div>
      )}

      {(state === 'idle' || state === 'enabling') && (
        <div className="space-y-3">
          {education}
          <button
            type="button"
            onClick={handleEnable}
            disabled={state === 'enabling' || !businessId}
            className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-black disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {state === 'enabling' ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Setting up — follow the Apple prompts…</>
            ) : (
              <><Smartphone className="h-4 w-4 mr-2" />Enable Tap to Pay on iPhone</>
            )}
          </button>
          {state === 'enabling' && (
            <p className="text-xs text-gray-400 text-center">
              Apple will ask you to accept the Tap to Pay terms, then configure this iPhone. This can take a minute the first time.
            </p>
          )}
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
      )}

      {state === 'ready' && (
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-green-800">Tap to Pay is ready on this iPhone</p>
              <p className="text-xs text-green-600">Choose Tap to Pay when recording a payment to charge a customer.</p>
            </div>
          </div>
          {education}
          <button
            type="button"
            onClick={handleEnable}
            className="w-full inline-flex items-center justify-center px-4 py-2 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Run setup again
          </button>
        </div>
      )}
    </div>
  );
};

export default TapToPaySetup;
